import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { format, parseISO } from 'date-fns';

interface SleepVitalsChartProps {
  data: any[];
}

type SleepMetric = 'duration' | 'quality' | 'stages' | 'physiological' | 'hrv';

const SleepVitalsChart: React.FC<SleepVitalsChartProps> = ({ data }) => {
  const [selectedMetric, setSelectedMetric] = useState<SleepMetric>('duration');

  if (!data || data.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Sleep Trends</h2>
        <div className="text-center py-8 text-gray-500">
          No sleep data available yet
        </div>
      </div>
    );
  }

  // Transform data for charts
  const chartData = data
    .sort((a, b) => new Date(a.sleep_start).getTime() - new Date(b.sleep_start).getTime())
    .map(summary => ({
      date: format(parseISO(summary.sleep_start), 'MMM dd'),
      fullDate: summary.sleep_start,
      // Duration metrics (in hours)
      totalSleep: summary.duration_in_sleep / 3600,
      deepSleep: summary.duration_in_deep / 3600,
      remSleep: summary.duration_in_rem / 3600,
      // Quality metrics
      sleepScore: summary.sleep_score,
      bedExits: summary.bedexit_count,
      // Physiological metrics
      avgHr: summary.avg_hr ? parseFloat(summary.avg_hr) : null,
      avgRr: summary.avg_rr ? parseFloat(summary.avg_rr) : null,
      hrvMorning: summary.hrv_rmssd_morning ? parseFloat(summary.hrv_rmssd_morning) : null,
      hrvEvening: summary.hrv_rmssd_evening ? parseFloat(summary.hrv_rmssd_evening) : null,
    }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value} {entry.unit || ''}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderDurationChart = () => (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 12 }}
          stroke="#6b7280"
        />
        <YAxis
          tick={{ fontSize: 12 }}
          stroke="#6b7280"
          label={{ value: 'Hours', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <ReferenceLine y={7} stroke="#10b981" strokeDasharray="3 3" label={{ value: 'Target: 7h', fontSize: 10 }} />
        <ReferenceLine y={5.5} stroke="#ef4444" strokeDasharray="3 3" label={{ value: 'Min: 5.5h', fontSize: 10 }} />
        <Line
          type="monotone"
          dataKey="totalSleep"
          stroke="#3b82f6"
          strokeWidth={2}
          name="Total Sleep"
          unit="h"
          dot={{ r: 4 }}
          activeDot={{ r: 6 }}
        />
        <Line
          type="monotone"
          dataKey="deepSleep"
          stroke="#8b5cf6"
          strokeWidth={2}
          name="Deep Sleep"
          unit="h"
          dot={{ r: 4 }}
          activeDot={{ r: 6 }}
        />
        <Line
          type="monotone"
          dataKey="remSleep"
          stroke="#ec4899"
          strokeWidth={2}
          name="REM Sleep"
          unit="h"
          dot={{ r: 4 }}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );

  const renderQualityChart = () => (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 12 }}
          stroke="#6b7280"
        />
        <YAxis
          yAxisId="score"
          tick={{ fontSize: 12 }}
          stroke="#6b7280"
          label={{ value: 'Sleep Score', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
          domain={[0, 100]}
        />
        <YAxis
          yAxisId="exits"
          orientation="right"
          tick={{ fontSize: 12 }}
          stroke="#6b7280"
          label={{ value: 'Bed Exits', angle: 90, position: 'insideRight', style: { fontSize: 12 } }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <ReferenceLine yAxisId="score" y={70} stroke="#10b981" strokeDasharray="3 3" label={{ value: 'Good: 70', fontSize: 10 }} />
        <Line
          yAxisId="score"
          type="monotone"
          dataKey="sleepScore"
          stroke="#10b981"
          strokeWidth={2}
          name="Sleep Score"
          unit="/100"
          dot={{ r: 4 }}
          activeDot={{ r: 6 }}
        />
        <Line
          yAxisId="exits"
          type="monotone"
          dataKey="bedExits"
          stroke="#f59e0b"
          strokeWidth={2}
          name="Bed Exits"
          unit=" exits"
          dot={{ r: 4 }}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );

  const renderStagesChart = () => {
    const stagePercentageData = chartData.map(d => ({
      ...d,
      deepPercent: (d.deepSleep / d.totalSleep) * 100,
      remPercent: (d.remSleep / d.totalSleep) * 100,
    }));

    return (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={stagePercentageData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            stroke="#6b7280"
          />
          <YAxis
            tick={{ fontSize: 12 }}
            stroke="#6b7280"
            label={{ value: 'Percentage', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
            domain={[0, 100]}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <ReferenceLine y={20} stroke="#8b5cf6" strokeDasharray="3 3" label={{ value: 'Deep Target: 20%', fontSize: 10 }} />
          <ReferenceLine y={25} stroke="#ec4899" strokeDasharray="3 3" label={{ value: 'REM Target: 25%', fontSize: 10 }} />
          <Line
            type="monotone"
            dataKey="deepPercent"
            stroke="#8b5cf6"
            strokeWidth={2}
            name="Deep Sleep %"
            unit="%"
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
          <Line
            type="monotone"
            dataKey="remPercent"
            stroke="#ec4899"
            strokeWidth={2}
            name="REM Sleep %"
            unit="%"
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const renderPhysiologicalChart = () => (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 12 }}
          stroke="#6b7280"
        />
        <YAxis
          yAxisId="hr"
          tick={{ fontSize: 12 }}
          stroke="#6b7280"
          label={{ value: 'Heart Rate (bpm)', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
          domain={[50, 90]}
        />
        <YAxis
          yAxisId="rr"
          orientation="right"
          tick={{ fontSize: 12 }}
          stroke="#6b7280"
          label={{ value: 'Resp. Rate (br/min)', angle: 90, position: 'insideRight', style: { fontSize: 12 } }}
          domain={[12, 24]}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Line
          yAxisId="hr"
          type="monotone"
          dataKey="avgHr"
          stroke="#ef4444"
          strokeWidth={2}
          name="Avg Heart Rate"
          unit=" bpm"
          dot={{ r: 4 }}
          activeDot={{ r: 6 }}
        />
        <Line
          yAxisId="rr"
          type="monotone"
          dataKey="avgRr"
          stroke="#06b6d4"
          strokeWidth={2}
          name="Avg Resp. Rate"
          unit=" br/min"
          dot={{ r: 4 }}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );

  const renderHrvChart = () => (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 12 }}
          stroke="#6b7280"
        />
        <YAxis
          tick={{ fontSize: 12 }}
          stroke="#6b7280"
          label={{ value: 'HRV (ms)', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
          domain={[0, 120]}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <ReferenceLine y={50} stroke="#10b981" strokeDasharray="3 3" label={{ value: 'Good: 50ms', fontSize: 10 }} />
        <ReferenceLine y={30} stroke="#f59e0b" strokeDasharray="3 3" label={{ value: 'Low: 30ms', fontSize: 10 }} />
        <Line
          type="monotone"
          dataKey="hrvMorning"
          stroke="#8b5cf6"
          strokeWidth={2}
          name="HRV Morning"
          unit=" ms"
          dot={{ r: 4 }}
          activeDot={{ r: 6 }}
          connectNulls
        />
        <Line
          type="monotone"
          dataKey="hrvEvening"
          stroke="#06b6d4"
          strokeWidth={2}
          name="HRV Evening"
          unit=" ms"
          dot={{ r: 4 }}
          activeDot={{ r: 6 }}
          connectNulls
        />
      </LineChart>
    </ResponsiveContainer>
  );

  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-900">Sleep Trends Over Time</h2>
        <div className="text-sm text-gray-500">
          {chartData.length} nights of data
        </div>
      </div>

      {/* Metric Selector */}
      <div className="flex space-x-2 mb-4 overflow-x-auto">
        <button
          onClick={() => setSelectedMetric('duration')}
          className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
            selectedMetric === 'duration'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Sleep Duration
        </button>
        <button
          onClick={() => setSelectedMetric('quality')}
          className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
            selectedMetric === 'quality'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Sleep Quality
        </button>
        <button
          onClick={() => setSelectedMetric('stages')}
          className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
            selectedMetric === 'stages'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Sleep Stages %
        </button>
        <button
          onClick={() => setSelectedMetric('physiological')}
          className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
            selectedMetric === 'physiological'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Heart & Respiration
        </button>
        <button
          onClick={() => setSelectedMetric('hrv')}
          className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
            selectedMetric === 'hrv'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          HRV Trends
        </button>
      </div>

      {/* Chart Display */}
      <div className="mt-4">
        {selectedMetric === 'duration' && renderDurationChart()}
        {selectedMetric === 'quality' && renderQualityChart()}
        {selectedMetric === 'stages' && renderStagesChart()}
        {selectedMetric === 'physiological' && renderPhysiologicalChart()}
        {selectedMetric === 'hrv' && renderHrvChart()}
      </div>

      {/* Chart Legend/Info */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          {selectedMetric === 'duration' && (
            <>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-gray-600">Total Sleep: Time spent asleep</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <span className="text-gray-600">Deep Sleep: Restorative sleep stage</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-pink-500 rounded-full"></div>
                <span className="text-gray-600">REM Sleep: Dream and memory consolidation</span>
              </div>
            </>
          )}
          {selectedMetric === 'quality' && (
            <>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-gray-600">Sleep Score: Overall sleep quality (0-100)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                <span className="text-gray-600">Bed Exits: Number of times out of bed</span>
              </div>
            </>
          )}
          {selectedMetric === 'stages' && (
            <>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <span className="text-gray-600">Deep Sleep %: Target ~20% of total sleep</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-pink-500 rounded-full"></div>
                <span className="text-gray-600">REM Sleep %: Target ~25% of total sleep</span>
              </div>
            </>
          )}
          {selectedMetric === 'physiological' && (
            <>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-gray-600">Heart Rate: Average during sleep</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-cyan-500 rounded-full"></div>
                <span className="text-gray-600">Respiration Rate: Breaths per minute</span>
              </div>
            </>
          )}
          {selectedMetric === 'hrv' && (
            <>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <span className="text-gray-600">HRV Morning: Heart rate variability upon waking</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-cyan-500 rounded-full"></div>
                <span className="text-gray-600">HRV Evening: Heart rate variability at bedtime</span>
              </div>
              <div className="col-span-2 text-xs text-gray-500 mt-2">
                Higher HRV indicates better recovery and parasympathetic activity. Values above 50ms are generally considered good.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SleepVitalsChart;
